import i18n from "i18next";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import { useDeleteDocument, useDuplicateDocument, useRestoreDocument } from "@/hooks/useDocuments";
import { useToast } from "@/components/shared/Toast";

/** How long Undo is offered after Move to Trash (documents-actions handoff §1). */
export const TRASH_UNDO_MS = 8000;

/**
 * Rename · Duplicate · Move to Trash, as the card's ⋯ and the editor's ⋯ both
 * offer them (Olivia, documents-actions handoff, 26 Sep). Trash asks nothing:
 * the toast's Undo and the Trash page are two ways back.
 */
export function useDocActions(opts: { onDuplicated?: (id: string) => void } = {}) {
  const { fetchWithAuth } = useApiContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const duplicate = useDuplicateDocument();
  const trash = useDeleteDocument();
  const restore = useRestoreDocument();
  const { onDuplicated } = opts;

  const rename = useCallback(async (id: string, title: string) => {
    const res = await fetchWithAuth(`${API_BASE}/documents/${id}`, {
      method: "PUT",
      body: JSON.stringify({ title }),
    });
    await handleResponse(res);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["documents"] }),
      queryClient.invalidateQueries({ queryKey: ["document", id] }),
    ]);
  }, [fetchWithAuth, queryClient]);

  const duplicateDoc = useCallback(async (id: string) => {
    const { document } = await duplicate.mutateAsync(id);
    onDuplicated?.(document.id);
    showToast(i18n.t("documents:library.toast.duplicated"), "success", {
      action: { key: "open", label: i18n.t("documents:library.toast.open"), onClick: () => navigate(`/d/${document.id}`) },
    });
    return document.id;
  }, [duplicate, navigate, onDuplicated, showToast]);

  /** `reopen`: Undo also opens the document again (trash from the editor). */
  const trashDoc = useCallback(async (id: string, { reopen = false } = {}) => {
    await trash.mutateAsync(id);
    showToast(i18n.t("documents:library.toast.trashed"), "info", {
      durationMs: TRASH_UNDO_MS,
      action: {
        key: "undo",
        label: i18n.t("documents:library.toast.undo"),
        onClick: () => {
          void restore.mutateAsync(id).then(() => { if (reopen) navigate(`/d/${id}`); });
        },
      },
    });
  }, [trash, restore, navigate, showToast]);

  /**
   * "Remove from my documents" — a reader takes a shared document off their
   * own list. Nothing is deleted; Undo puts it back (Olivia, 26 Sep: the same
   * toast as Trash). `reopen`: Undo also opens it again (from the editor).
   */
  const removeDoc = useCallback(async (id: string, { reopen = false } = {}) => {
    await handleResponse(await fetchWithAuth(`${API_BASE}/documents/${id}/hide`, { method: "POST" }));
    await queryClient.invalidateQueries({ queryKey: ["documents"] });
    showToast(i18n.t("documents:library.toast.removed"), "info", {
      durationMs: TRASH_UNDO_MS,
      action: {
        key: "undo",
        label: i18n.t("documents:library.toast.undo"),
        onClick: () => {
          void fetchWithAuth(`${API_BASE}/documents/${id}/hide`, { method: "DELETE" })
            .then(() => queryClient.invalidateQueries({ queryKey: ["documents"] }))
            .then(() => { if (reopen) navigate(`/d/${id}`); });
        },
      },
    });
  }, [fetchWithAuth, queryClient, showToast, navigate]);

  return { rename, duplicateDoc, trashDoc, removeDoc };
}

/** A new title, or null to keep the old one (empty or unchanged reverts). */
export function renamedTitle(draft: string, current: string): string | null {
  const next = draft.trim();
  return next === "" || next === current ? null : next;
}
